import type { WeldJoint, WeldStepPanelItem, WeldStepPanels } from '../../SpoolsProduction';
import type { SpoolTrackerWeldStepType } from '../../spoolNcrApi';
import type { TriStateCheck } from './spoolDetailFormUi';

function panelArrayRevision(rows: WeldStepPanelItem[] | undefined): string {
  return (rows ?? []).map((n) => `${n.id ?? ''}-${n.updatedAt ?? ''}`).join(',');
}

/** Bumps when any step panel row for this joint changes (for useEffect deps). */
export function weldPanelsRevision(weld: WeldJoint | null): string {
  if (!weld?.weldStepPanels) return '';
  const p = weld.weldStepPanels;
  return [
    panelArrayRevision(p.fitUp),
    panelArrayRevision(p.preheat),
    panelArrayRevision(p.welding),
    panelArrayRevision(p.vt),
    panelArrayRevision(p.ndt),
    panelArrayRevision(p.coating),
    panelArrayRevision(p.accept),
  ].join('|');
}

type StepPanelKey = 'fitUp' | 'preheat' | 'welding' | 'vt' | 'ndt' | 'coating' | 'accept';

/** Latest row’s payload for this step kind (same rule as server: last id wins for display). */
export function getLastStepPanelPayload(
  weld: WeldJoint | null,
  key: StepPanelKey
): Record<string, unknown> | null {
  if (!weld?.weldStepPanels) return null;
  const raw = weld.weldStepPanels[key];
  const rows = Array.isArray(raw) ? raw : [];
  if (rows.length === 0) return null;
  const last = rows[rows.length - 1];
  if (!last?.payload || typeof last.payload !== 'object') return null;
  return last.payload as Record<string, unknown>;
}

/** All welding step rows for this joint (API order, ascending id). */
export function getAllWeldingPanelRows(weld: WeldJoint | null): WeldStepPanelItem[] {
  const arr = weld?.weldStepPanels?.welding;
  return Array.isArray(arr) ? arr : [];
}

function weldingStepKindFromPayload(
  pl: Record<string, unknown> | null | undefined
): 'welding' | 'repair_weld' {
  const k = pl?.weldingStepKind ?? pl?.welding_step_kind;
  if (k == null || k === '') return 'welding';
  return String(k).toLowerCase() === 'repair_weld' ? 'repair_weld' : 'welding';
}

/** Last non-repair welding row — drives primary Weld tab + workflow “past welding” checks. */
export function getLastPrimaryWeldingPanelItem(weld: WeldJoint | null): WeldStepPanelItem | null {
  const rows = getAllWeldingPanelRows(weld);
  const primary = rows.filter((r) => {
    const pl = r.payload && typeof r.payload === 'object' ? (r.payload as Record<string, unknown>) : null;
    return weldingStepKindFromPayload(pl) !== 'repair_weld';
  });
  if (primary.length === 0) return null;
  return primary[primary.length - 1];
}

/** Payload for the primary weld record only (`weldingStepKind !== 'repair_weld'`). */
export function getPrimaryWeldingPanelPayload(weld: WeldJoint | null): Record<string, unknown> | null {
  const row = getLastPrimaryWeldingPanelItem(weld);
  if (!row?.payload || typeof row.payload !== 'object') return null;
  return row.payload as Record<string, unknown>;
}

/** Payload for a dedicated repair weld row (GET returns multiple `welding[]` rows). */
export function getRepairWeldingPanelPayload(
  weld: WeldJoint | null,
  repairNo: number | null | undefined
): Record<string, unknown> | null {
  if (repairNo == null) return null;
  const rows = getAllWeldingPanelRows(weld);
  for (let i = rows.length - 1; i >= 0; i--) {
    const r = rows[i];
    const pl = r.payload && typeof r.payload === 'object' ? (r.payload as Record<string, unknown>) : null;
    if (!pl || weldingStepKindFromPayload(pl) !== 'repair_weld') continue;
    const rl = pl.repairLink;
    if (rl && typeof rl === 'object') {
      const n = (rl as Record<string, unknown>).repairNo ?? (rl as Record<string, unknown>).repair_no;
      if (n != null && Number(n) === repairNo) return pl;
    }
  }
  return null;
}

/**
 * Last dedicated `repair_weld` row in `welding[]` — infer `repairNo` / `stepPanelRowId` after GET when
 * modal has no `repairNcrContext`, and decide UPDATE vs INSERT on save.
 */
export function getLastDedicatedRepairWeldingInfo(weld: WeldJoint | null): {
  stepPanelRowId: number;
  repairNo: number;
  payload: Record<string, unknown>;
} | null {
  const rows = getAllWeldingPanelRows(weld);
  for (let i = rows.length - 1; i >= 0; i--) {
    const r = rows[i];
    const pl = r.payload && typeof r.payload === 'object' ? (r.payload as Record<string, unknown>) : null;
    if (!pl || weldingStepKindFromPayload(pl) !== 'repair_weld') continue;
    const rl = pl.repairLink;
    let num = 1;
    if (rl && typeof rl === 'object') {
      const n = (rl as Record<string, unknown>).repairNo ?? (rl as Record<string, unknown>).repair_no;
      if (n != null && !Number.isNaN(Number(n))) num = Number(n);
    }
    return { stepPanelRowId: r.id, repairNo: num, payload: pl };
  }
  return null;
}

/**
 * If `welding[]` includes a `repair_weld` row (GET / getAllSpools), infer NCR source step so the
 * integrated strip shows Repair Weld + VT/NDT retest without requiring a local “Add repair” click.
 */
export function inferIntegratedRepairSourceFromWeld(
  weld: WeldJoint | null
): SpoolTrackerWeldStepType | null {
  const rows = getAllWeldingPanelRows(weld);
  const repairRows = rows.filter((r) => {
    const pl = r.payload && typeof r.payload === 'object' ? (r.payload as Record<string, unknown>) : null;
    return weldingStepKindFromPayload(pl) === 'repair_weld';
  });
  if (repairRows.length === 0) return null;
  const last = repairRows[repairRows.length - 1];
  const pl = last.payload && typeof last.payload === 'object' ? (last.payload as Record<string, unknown>) : null;
  if (!pl) return 'welding';
  const rl = pl.repairLink;
  if (rl && typeof rl === 'object') {
    const raw = (rl as Record<string, unknown>).sourceStep ?? (rl as Record<string, unknown>).source_step;
    const s = raw != null ? String(raw).toLowerCase().trim() : 'welding';
    if (s === 'vt' || s.includes('visual')) return 'vt';
    if (s === 'ndt') return 'ndt';
    if (s === 'welding' || s === 'weld') return 'welding';
  }
  return 'welding';
}

/** Literal last welding row payload (may be a repair cycle row). */
export function getLastWeldingPanelPayload(weld: WeldJoint | null): Record<string, unknown> | null {
  const rows = getAllWeldingPanelRows(weld);
  if (rows.length === 0) return null;
  const last = rows[rows.length - 1];
  if (!last?.payload || typeof last.payload !== 'object') return null;
  return last.payload as Record<string, unknown>;
}

/**
 * Raw JSON `payload` for a step. For **`welding`**, uses the **primary** weld row only so repair rows
 * appended to `welding[]` do not overwrite the Weld tab / signatures.
 */
export function getWeldStepPanelPayload(
  weld: WeldJoint | null,
  key: StepPanelKey
): Record<string, unknown> | null {
  if (key === 'welding') {
    return getPrimaryWeldingPanelPayload(weld);
  }
  return getLastStepPanelPayload(weld, key);
}

/**
 * When the welding step `payload` includes `repairs[]` (append-only repair cycles), return the
 * object for `repairNo` so the Repair Weld panel can hydrate after save/refetch.
 */
export function findRepairWeldEntryInWeldingPayload(
  weld: WeldJoint | null,
  repairNo: number | null
): Record<string, unknown> | null {
  if (repairNo == null) return null;
  const dedicated = getRepairWeldingPanelPayload(weld, repairNo);
  if (dedicated && Object.keys(dedicated).length > 0) return dedicated;
  const pl = getPrimaryWeldingPanelPayload(weld);
  if (!pl) return null;
  const raw = pl.repairs ?? pl.repairsDraft;
  if (!Array.isArray(raw)) return null;
  for (const item of raw) {
    if (item && typeof item === 'object') {
      const o = item as Record<string, unknown>;
      const n = o.repairNo ?? o.repair_no;
      if (n != null && Number(n) === repairNo) return o;
    }
  }
  return null;
}

/** All VT step rows for this joint (API order, ascending `id`). */
export function getAllVtPanelRows(weld: WeldJoint | null): WeldStepPanelItem[] {
  const arr = weld?.weldStepPanels?.vt;
  return Array.isArray(arr) ? (arr as WeldStepPanelItem[]) : [];
}

/** All NDT step rows for this joint (in API order). */
export function getAllNdtPanelRows(
  weld: WeldJoint | null
): WeldStepPanelItem[] {
  const arr = weld?.weldStepPanels?.ndt;
  return Array.isArray(arr) ? (arr as WeldStepPanelItem[]) : [];
}

/** Last NDT line for this joint (chronological by `id` on the API DTO). */
export function getLastNdtPanelPayload(
  weld: WeldJoint | null
): Record<string, unknown> | null {
  return getLastStepPanelPayload(weld, 'ndt');
}

export function apiTriToUi(v: unknown): TriStateCheck {
  if (v === 'pass') return 'pass';
  if (v === 'fail') return 'fail';
  return 'neutral';
}

/** Same rules as server `isSubmittedMode` / `deriveJointWorkflowStatus` — per joint only, never from spool. */
function isPanelSubmittedMode(m: string | undefined | null): boolean {
  if (m == null) return false;
  const s = m.trim().toLowerCase();
  return (
    s === 'submitted' ||
    s === 'submit' ||
    s === 'final' ||
    s === 'complete' ||
    s === 'confirm_weld' ||
    s === 'pass_vt' ||
    s === 'pass_release' ||
    s === 'accept_complete'
  );
}

/**
 * Current workflow step for this row, from that joint’s `weldStepPanels` only.
 * Use when the API omits `workflowStatus` on a joint (table then still matches the server’s per-weld model).
 */
export function deriveWorkflowKeyFromWeldStepPanels(
  panels: WeldStepPanels | null | undefined
):
  | 'fitup'
  | 'preheat'
  | 'welding'
  | 'vt'
  | 'ndt'
  | 'coating'
  | 'accept'
  | 'pending' {
  if (!panels) return 'pending';
  const accept = panels.accept ?? [];
  if (accept.length && accept.some((r) => isPanelSubmittedMode(r.submissionMode))) return 'accept';
  if (accept.length) return 'accept';
  const coating = panels.coating ?? [];
  if (coating.length && coating.some((r) => isPanelSubmittedMode(r.submissionMode))) return 'coating';
  const ndt = panels.ndt ?? [];
  if (ndt.length && ndt.some((r) => isPanelSubmittedMode(r.submissionMode))) return 'ndt';
  const vt = panels.vt ?? [];
  if (vt.length && vt.some((r) => isPanelSubmittedMode(r.submissionMode))) return 'vt';
  const welding = panels.welding ?? [];
  if (welding.length && welding.some((r) => isPanelSubmittedMode(r.submissionMode))) return 'welding';
  if (welding.length) return 'welding';
  const preheat = panels.preheat ?? [];
  if (preheat.length && preheat.some((r) => isPanelSubmittedMode(r.submissionMode))) return 'preheat';
  if (preheat.length) return 'preheat';
  const fitUp = panels.fitUp ?? [];
  if (fitUp.length && fitUp.some((r) => isPanelSubmittedMode(r.submissionMode))) return 'fitup';
  if (fitUp.length) return 'fitup';
  return 'pending';
}

const WORKFLOW_FALLBACK_KEYS = new Set([
  'fitup',
  'preheat',
  'welding',
  'vt',
  'ndt',
  'coating',
  'accept',
  'pending',
]);

/**
 * Row workflow chip: **per-weld `weldStepPanels` wins**. API `workflowStatus` is only used when
 * panels do not resolve a step, except `accept` — that must never show unless this joint has
 * `weldStepPanels.accept` (avoids spool-level `accept` on every row).
 */
export function effectiveWeldWorkflowKey(
  weld: { weldStepPanels?: WeldStepPanels | null; workflowStatus?: string | null }
):
  | 'fitup'
  | 'preheat'
  | 'welding'
  | 'vt'
  | 'ndt'
  | 'coating'
  | 'accept'
  | 'pending' {
  const derived = deriveWorkflowKeyFromWeldStepPanels(weld.weldStepPanels);
  if (derived !== 'pending') {
    return derived;
  }
  const raw = String(weld.workflowStatus ?? '')
    .trim()
    .toLowerCase();
  if (raw === 'accept' && !(weld.weldStepPanels?.accept?.length ?? 0)) {
    return 'pending';
  }
  if (raw && WORKFLOW_FALLBACK_KEYS.has(raw)) {
    return raw as
      | 'fitup'
      | 'preheat'
      | 'welding'
      | 'vt'
      | 'ndt'
      | 'coating'
      | 'accept'
      | 'pending';
  }
  return 'pending';
}

/**
 * Which spool weld-step row this VT save updates — aligns with server multi-row VT + retest inserts.
 * Primary VT updates the **first** row; VT retest defaults to the **latest** row so edits update instead
 * of inserting when payload omits `vtPhase` (legacy rows).
 */
export function resolveVtStepPanelRowId(
  weld: WeldJoint | null,
  vtPhase: 'standard' | 'retest'
): number | undefined {
  const rows = weld?.weldStepPanels?.vt;
  const arr = Array.isArray(rows) ? rows : [];
  if (arr.length === 0) return undefined;

  if (vtPhase === 'standard') {
    return arr[0].id;
  }

  const last = arr[arr.length - 1];
  return last.id;
}

/** Payload for the VT row targeted by main VT (first row) vs repair VT retest (last row) — no UI picker. */
export function getVtPanelPayloadForPhase(
  weld: WeldJoint | null,
  vtPhase: 'standard' | 'retest'
): Record<string, unknown> | null {
  const rows = getAllVtPanelRows(weld);
  if (rows.length === 0) return null;
  const id = resolveVtStepPanelRowId(weld, vtPhase);
  const row = id != null ? rows.find((r) => r.id === id) : undefined;
  const target = row ?? (vtPhase === 'standard' ? rows[0] : rows[rows.length - 1]);
  const pl = target?.payload;
  if (!pl || typeof pl !== 'object') return null;
  return pl as Record<string, unknown>;
}

/** Same targeting as `getVtPanelPayloadForPhase`, but returns the panel row (e.g. for `updatedAt`). */
export function getVtPanelRowForPhase(
  weld: WeldJoint | null,
  vtPhase: 'standard' | 'retest'
): WeldStepPanelItem | null {
  const rows = getAllVtPanelRows(weld);
  if (rows.length === 0) return null;
  const id = resolveVtStepPanelRowId(weld, vtPhase);
  const row = id != null ? rows.find((r) => r.id === id) : undefined;
  const target = row ?? (vtPhase === 'standard' ? rows[0] : rows[rows.length - 1]);
  return target ?? null;
}

/** Latest coating weld-step row (same as payload source for `getWeldStepPanelPayload(weld, 'coating')`). */
export function getLastCoatingPanelRow(weld: WeldJoint | null): WeldStepPanelItem | null {
  const arr = weld?.weldStepPanels?.coating;
  const rows = Array.isArray(arr) ? arr : [];
  if (rows.length === 0) return null;
  return rows[rows.length - 1] ?? null;
}

/** Prefer NDT row tagged with this repair cycle; else latest row by id (retest strip default edit target). */
export function resolveNdtRetestStepPanelRowId(
  weld: WeldJoint | null,
  repairNo: number | null | undefined
): number | undefined {
  const rows = getAllNdtPanelRows(weld);
  if (rows.length === 0) return undefined;
  if (repairNo != null && !Number.isNaN(Number(repairNo))) {
    const n = Number(repairNo);
    for (let i = rows.length - 1; i >= 0; i--) {
      const pl = rows[i]?.payload as Record<string, unknown> | null | undefined;
      if (!pl) continue;
      const rn = pl.repairNo ?? pl.repair_no;
      if (rn != null && Number(rn) === n) return rows[i].id;
    }
  }
  return rows[rows.length - 1].id;
}

function ndtMethodKeyFromRow(row: WeldStepPanelItem): 'rt' | 'ut' | 'mpi' {
  const pl = row.payload;
  if (!pl || typeof pl !== 'object') return 'rt';
  const p = pl as Record<string, unknown>;
  const raw = p.ndtMethod ?? p.ndt_method ?? 'rt';
  const s = String(raw).toLowerCase();
  if (s === 'ut' || s.includes('ultrasonic')) return 'ut';
  if (s === 'mpi' || s.includes('magnetic') || s.includes('mt')) return 'mpi';
  return 'rt';
}

function normalizeNdtMethodKey(ndtMethod: string): 'rt' | 'ut' | 'mpi' {
  const s = String(ndtMethod ?? 'rt').toLowerCase();
  if (s === 'ut' || s.includes('ultrasonic')) return 'ut';
  if (s === 'mpi' || s.includes('magnetic') || s.includes('mt')) return 'mpi';
  return 'rt';
}

/** Standard NDT strip: one DB row per method (RT / UT / MPI) — match form method to existing row id. */
export function resolveNdtStandardStepPanelRowId(
  weld: WeldJoint | null,
  ndtMethod: string
): number | undefined {
  const rows = getAllNdtPanelRows(weld);
  const want = normalizeNdtMethodKey(ndtMethod);
  for (let i = rows.length - 1; i >= 0; i--) {
    if (ndtMethodKeyFromRow(rows[i]) === want) return rows[i].id;
  }
  return undefined;
}

/** Hydrate NDT form from the row targeted by main vs repair retest (no submitted-rows table). */
export function getNdtPanelPayloadForPhase(
  weld: WeldJoint | null,
  ndtPhase: 'standard' | 'retest',
  ndtMethod: string,
  repairNo: number | null | undefined
): Record<string, unknown> | null {
  const rows = getAllNdtPanelRows(weld);
  if (rows.length === 0) return null;
  if (ndtPhase === 'retest') {
    const id = resolveNdtRetestStepPanelRowId(weld, repairNo);
    const row = id != null ? rows.find((r) => r.id === id) : rows[rows.length - 1];
    const pl = row?.payload;
    if (!pl || typeof pl !== 'object') return null;
    return pl as Record<string, unknown>;
  }
  const id = resolveNdtStandardStepPanelRowId(weld, ndtMethod);
  const row = id != null ? rows.find((r) => r.id === id) : undefined;
  const pl = row?.payload;
  if (!pl || typeof pl !== 'object') return null;
  return pl as Record<string, unknown>;
}

export function resolveWeldingStepPanelRowId(
  weld: WeldJoint | null,
  panelMode: 'welding' | 'repair_weld',
  repairNo: number | null | undefined
): number | undefined {
  const rows = weld?.weldStepPanels?.welding;
  const arr = Array.isArray(rows) ? rows : [];
  if (panelMode === 'repair_weld') {
    // Explicit repair cycle (including new repair from NCR): match row by repairNo — INSERT when none found.
    if (repairNo != null && !Number.isNaN(Number(repairNo))) {
      const tn = Number(repairNo);
      for (let i = arr.length - 1; i >= 0; i--) {
        const pl = arr[i]?.payload as Record<string, unknown> | null | undefined;
        if (!pl || String(pl.weldingStepKind ?? '').toLowerCase() !== 'repair_weld') continue;
        const rl = pl.repairLink as Record<string, unknown> | undefined;
        if (rl != null && Number(rl.repairNo ?? rl.repair_no) === tn) {
          return arr[i].id;
        }
      }
      return undefined;
    }
    // Refetch / strip navigation without NCR context: update last persisted repair row only.
    const lastInfo = getLastDedicatedRepairWeldingInfo(weld);
    return lastInfo?.stepPanelRowId;
  }
  const primary = getLastPrimaryWeldingPanelItem(weld);
  return primary?.id;
}

